import { isBlank } from './detailShared'
// S422 — la línea de tiempo de ESTADOS de una operación, sacada entera de `statusChanges`.
//
// ⚠️⚠️ SUSTITUYE A LA FRANJA DE OPERARIOS, Y LA PREGUNTA NO ES LA MISMA. Aquella salía de
// `interactions` (`device_worker_fabrication_order`) y contestaba «QUIÉN TRABAJÓ Y CUÁNTO»: un carril
// por operario. Esta sale de `device_fabrication_order` y contesta «QUIÉN CAMBIÓ EL ESTADO Y POR
// QUÉ»: un tramo por estado. Decisión del PO del 23/09: «dejamos solo lo que se pueda sacar por la
// nueva tabla intentando maximizarlo lo máximo posible».
//
// 📌 LOS CARRILES POR OPERARIO NO SE SIMULAN, y la tentación está a mano: cada tramo trae `openedBy`
// y `closedBy`. Pero quien CAMBIA un estado no es quien TRABAJA el tramo — uno deja la máquina en
// parada y otro la saca, y entre medias puede no haber nadie—. Agrupar por `openedBy` dibujaría un
// «quién trabajó» inventado con un dato que dice otra cosa. Los carriles, si hay más de uno, son por
// MÁQUINA, que sí es una propiedad del tramo.
//
// ⚠️⚠️ LA DURACIÓN SE CALCULA AQUÍ, Y NO TIENE POR QUÉ CUADRAR CON EL TIEMPO REAL DE LA OPERACIÓN.
// Esta tabla no trae ninguna duración; se calcula como `endedAt − changedAt`, que son dos hechos
// registrados. Pero es la duración de un TRAMO DE ESTADO, y el tiempo real de la operación sale de
// la OTRA tabla (`SUM(duration)` de `device_worker_fabrication_order`). Son dos preguntas distintas:
// si algún día alguien suma los tramos «en ejecución» de aquí y no le cuadra con el tiempo real de la
// cabecera, la respuesta es ésta — no es un error de ninguna de las dos.

const msOf = (value) => {
  if (isBlank(value)) return null
  const parsed = Date.parse(value)
  return Number.isFinite(parsed) ? parsed : null
}

// El mismo umbral de un minuto que decidió el PO para los tramos cortos (S401), ahora sobre tramos
// de estado. Un cambio de estado de pocos segundos es casi siempre un rebote del terminal.
export const SHORT_STATUS_CHANGE_SECONDS = 60

/**
 * La duración de un tramo CERRADO, en segundos, o `null`.
 *
 * ⚠️ Un tramo ABIERTO devuelve `null`, no la distancia hasta ahora: su duración NO ES FINAL. Una
 * cifra que crece cada vez que se abre la pantalla no es un dato, así que no entra en ninguna suma.
 */
export const closedDurationSeconds = (change) => {
  const row = change || {}
  if (row.open) return null
  const start = msOf(row.changedAt)
  const end = msOf(row.endedAt)
  if (start === null || end === null) return null
  return Math.max(0, Math.round((end - start) / 1000))
}

/**
 * ¿Es un tramo corto, de los que el filtro esconde?
 *
 * ⚠️⚠️ UN TRAMO ABIERTO NO ES CORTO JAMÁS. Su duración no es pequeña: es DESCONOCIDA. Esconderlo por
 * llevar pocos segundos abierto borraría justo lo que está pasando ahora mismo.
 */
export const isShortStatusChange = (change) => {
  const seconds = closedDurationSeconds(change)
  return seconds !== null && seconds < SHORT_STATUS_CHANGE_SECONDS
}

/**
 * Los tramos de estado, repartidos en carriles y sobre un eje común.
 *
 * ⚠️⚠️ EL EJE ACABA EN EL ÚLTIMO CAMBIO REGISTRADO, NO EN «AHORA», y es a propósito (decisión del PO
 * del 23/09, sobre datos reales). Un tramo abierto NO es trabajo en marcha: es el ESTADO ACTUAL, y
 * en la base dura semanas. En LTT001-HUN001, las 35 operaciones con actividad acaban TODAS en un
 * tramo abierto que lleva de 20 a 85 días así, y 7 de ellos son `FINISHED`. Con el eje hasta hoy,
 * en 17 de las 35 la historia registrada cabía en menos de un 25 % del eje, y en tres (jobs 4295,
 * 4298 y 4308) en el 0 %: la franja era una sola barra «Interrupted» de dos meses.
 * 📌 Así que NO «arregles» el eje para que llegue a hoy y sea proporcional hasta el final: lo sería,
 * y dejaría de enseñar lo único que la franja existe para enseñar. El tramo abierto llega hasta el
 * borde y se marca como que SIGUE (`continues`); cuánto lleva lo dice su «estado actual desde…».
 *
 * @param {Array} statusChanges los tramos tal como los sirve Sun — EN SU ORDEN, que no se toca:
 *   filas que empatan en la fecha harían que un cliente que pagine repita y pierda filas.
 * @param {{includeShort?: boolean}} options
 * @returns {{axis: {startMs: number|null, endMs: number|null}, lanes: Array, hiddenCount: number}}
 */
export const buildStatusTimeline = (statusChanges, { includeShort = false } = {}) => {
  const rows = (Array.isArray(statusChanges) ? statusChanges : []).map((change) => change || {})
  // S401 — el eje primero, con TODOS los tramos y sin condiciones: un tramo escondido sigue habiendo
  // ocurrido. Si el eje se encogiera al filtrar, al pulsar el control SALTARÍAN TODAS LAS BARRAS.
  // Un abierto aporta su INICIO, que es un cambio registrado; su fin no existe todavía.
  let axisStart = null
  let axisEnd = null
  rows.forEach((row) => {
    const startMs = msOf(row.changedAt)
    if (startMs === null) return
    const endMs = row.open ? startMs : (msOf(row.endedAt) ?? startMs)
    axisStart = axisStart === null ? startMs : Math.min(axisStart, startMs)
    axisEnd = axisEnd === null ? endMs : Math.max(axisEnd, endMs)
  })

  const lanes = new Map()
  let hiddenCount = 0
  rows.forEach((row) => {
    const startMs = msOf(row.changedAt)
    if (startMs === null) return // sin inicio no hay tramo que situar: no se inventa uno
    if (isShortStatusChange(row)) {
      hiddenCount += 1
      if (!includeShort) return
    }
    const laneKey = isBlank(row.deviceId) ? '' : String(row.deviceId)
    if (!lanes.has(laneKey)) lanes.set(laneKey, { deviceId: row.deviceId, deviceName: row.deviceName || '', segments: [] })
    lanes.get(laneKey).segments.push({
      key: row.id,
      startMs,
      // El abierto llega hasta el BORDE del eje —el último cambio registrado—: hasta ahí se sabe que
      // seguía en ese estado. Más allá no se mide; se marca que sigue.
      endMs: row.open ? axisEnd : msOf(row.endedAt),
      open: !!row.open,
      status: row.status || {},
      reason: row.reason || {},
      openedBy: row.openedBy || {},
      closedBy: row.closedBy || {},
      forced: row.forced === true
    })
  })

  return { axis: { startMs: axisStart, endMs: axisEnd }, lanes: Array.from(lanes.values()), hiddenCount }
}

/**
 * Cuánto tiempo pasó la operación en cada estado. Lo que más valor saca de la tabla nueva y hoy no
 * existe en ninguna parte.
 *
 * ⚠️⚠️ EL TRAMO ABIERTO NO SE SUMA: es el ESTADO ACTUAL, su duración no es final, y en la base dura
 * semanas — sumarlo inflaría cualquier total con tiempo de un estado que no ha acabado. Va APARTE,
 * en `openSegments`, como «estado actual desde…», uno por máquina.
 * 📌 Dos tramos del mismo estado se SUMAN — es la razón de ser del resumen — y el orden de los
 * estados es el de su primera aparición, heredado de cómo llegan: reordenar aquí sería una segunda
 * definición del mismo criterio.
 *
 * @returns {{totals: Array<{code: string, name: string, seconds: number}>, openSegments: Array<{code: string, name: string, since: string, deviceId: *, deviceName: string}>}}
 */
export const timeByStatus = (statusChanges) => {
  const rows = Array.isArray(statusChanges) ? statusChanges : []
  const totals = new Map()
  const openSegments = []
  rows.forEach((change) => {
    const row = change || {}
    const status = row.status || {}
    const code = isBlank(status.code) ? '' : String(status.code)
    if (row.open) {
      // Con la MÁQUINA: con varias, cada una tiene su estado actual, y dos «desde…» sueltos no
      // dirían de cuál es cada uno (jobs 4312 y 4316: FINISHED en una y en marcha en otra).
      openSegments.push({ code, name: status.name || code, since: row.changedAt, deviceId: row.deviceId, deviceName: row.deviceName || '' })
      return
    }
    const seconds = closedDurationSeconds(row)
    if (seconds === null) return
    if (!totals.has(code)) totals.set(code, { code, name: status.name || code, seconds: 0 })
    totals.get(code).seconds += seconds
  })
  return { totals: Array.from(totals.values()), openSegments }
}

// Los estados en los que la operación está EN MARCHA: ejecutando o preparando. Son los códigos del
// catálogo de MOS que ya usa `operationStatus.js`.
const ACTIVE_STATUS_CODES = ['ACTIVE_EXECUTION', 'ACTIVE_PREPARATION']

/**
 * S432 — «en marcha desde…»: el primer momento en que la operación pasó a un estado ACTIVO.
 *
 * ⚠️⚠️ SALE DE `statusChanges` Y NO DE `workers`, por decisión del PO del 23/09 («solo la tabla
 * nueva», elegido dos veces ese día). Antes era el `firstStart` más temprano de `workers`, que es
 * la tabla VIEJA de quién trabajó: con esta función `workers` deja de leerse del todo.
 * 📌 Se busca el primer paso a un estado ACTIVO, no el primer cambio de estado: el primer tramo
 * puede ser una espera o una interrupción heredada, y «en marcha desde» una parada sería falso.
 * Si la operación nunca llegó a un estado activo, NO hay «desde» y devuelve `''` — quien pinte lo
 * dice así, no inventa una fecha.
 * El orden es el que sirve Sun (fecha, desempate por `id`); se toma el menor por si acaso, porque
 * dos máquinas pueden arrancar la misma operación.
 *
 * @returns {string} el `changedAt` ISO, o `''`
 */
export const firstActiveSince = (statusChanges) => {
  const starts = (Array.isArray(statusChanges) ? statusChanges : [])
    .filter((change) => ACTIVE_STATUS_CODES.includes(((change || {}).status || {}).code))
    .map((change) => change.changedAt)
    .filter((value) => msOf(value) !== null)
    .sort((a, b) => msOf(a) - msOf(b))
  return starts[0] || ''
}
